import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { PERIOD_DAYS, PERIOD_MONTHS, PERIOD_QUARTERS, PERIOD_YEARS, PERIODS_MAP } from 'Constants/project_plan';
import moment from 'moment';

import { haveSameKeys } from 'Helpers/periodRange';
import { getTimeIntervals } from 'Helpers/timeIntervals';

const ProjectPeriodContext = createContext();

const PROJECT_PERIOD_KEY = 'project_period';

// Bumped to `_v3` because the header shape itself changed — one self-contained row per view instead
// of a year/month/day tier stack. The cache key describes the event and the format, not the shape,
// so without a new base key every browser that has already opened the Schedule tab would replay the
// old multi-row headers and never see the change.
//
// `_v2` did the same for a different reason: those entries were cached under a key that ignores the
// exclusions, so any browser that used the old per-period Exclude control held a TRUNCATED
// header/column set under a key claiming to describe the full timeline.
const PROJECT_PERIOD_HEADERS_BASE_KEY = 'project_period_headers_v3';
const PROJECT_PERIOD_COLUMNS_BASE_KEY = 'project_period_columns_v3';

const generatePeriodStorageKey = (baseKey, eventSlug, period, startDate, endDate) => {
    startDate = moment(startDate).unix();
    endDate = moment(endDate).unix();

    return `${baseKey}_${eventSlug}_${period}_${startDate}_${endDate}`;
};

const getPersistedPeriod = () => {
    return localStorage.getItem(PROJECT_PERIOD_KEY) || PERIOD_YEARS;
};

export const ProjectPeriodProvider = ({ minDate, maxDate, eventSlug, children }) => {
    const persistedPeriod = getPersistedPeriod();

    const [period, setPeriod] = useState(PERIODS_MAP.includes(persistedPeriod) ? persistedPeriod : PERIOD_YEARS);
    const [headers, setHeaders] = useState([]);
    const [columnsKeys, setColumnsKeys] = useState([]);
    const [excludedPeriods, setExcludedPeriods] = useState({});

    const periods = useMemo(() => getTimeIntervals(moment(minDate), moment(maxDate), period), [minDate, maxDate, period]);

    // Lookup from a column key (as used in `columnsKeys` and in the gantt
    // payload) to that interval's { start, end } moments, so a visible
    // column-index range can be translated into concrete Y-m-d dates for the
    // lazy column-window fetch (IB-814).
    const columnDateLookup = useMemo(() => {
        const lookup = {};

        if (!periods || !periods.years) {
            return lookup;
        }

        Object.entries(periods.years).forEach(([year, yearData]) => {
            if (period === PERIOD_YEARS) {
                lookup[year] = { start: yearData.start, end: yearData.end };
            } else if (period === PERIOD_QUARTERS) {
                Object.values(yearData.quarters || {}).forEach((quarter) => {
                    lookup[quarter.key] = { start: quarter.start, end: quarter.end };
                });
            } else if (period === PERIOD_MONTHS || period === PERIOD_DAYS) {
                Object.values(yearData.months || {}).forEach((month) => {
                    if (period === PERIOD_MONTHS) {
                        lookup[month.key] = { start: month.start, end: month.end };
                    } else {
                        Object.values(month.days || {}).forEach((day) => {
                            lookup[day.key] = { start: day.start, end: day.end };
                        });
                    }
                });
            }
        });

        return lookup;
    }, [periods, period]);

    // Translate a (visible) column-index range into a { from, to } Y-m-d date
    // range, clamped to the available columns. Indices are ordered by
    // `columnsKeys`; the range is inclusive of both bounds (IB-814).
    const getDateRangeForColumnIndexes = useCallback(
        (startIndex, endIndex) => {
            if (!columnsKeys.length) {
                return null;
            }

            const lastIndex = columnsKeys.length - 1;
            const clamp = (index) => Math.max(0, Math.min(index, lastIndex));
            const lo = clamp(Math.min(startIndex, endIndex));
            const hi = clamp(Math.max(startIndex, endIndex));

            const fromInterval = columnDateLookup[columnsKeys[lo]];
            const toInterval = columnDateLookup[columnsKeys[hi]];

            if (!fromInterval || !toInterval) {
                return null;
            }

            return {
                from: fromInterval.start.format('YYYY-MM-DD'),
                to: toInterval.end.format('YYYY-MM-DD'),
            };
        },
        [columnsKeys, columnDateLookup],
    );

    // The inverse of `getDateRangeForColumnIndexes`: which column a Y-m-d date lands in. Returns
    // the first column whose interval has not already ended, which resolves the three off-grid
    // cases as well as the ordinary one:
    //   - inside an excluded gap  -> the next visible column
    //   - earlier than every column -> 0, the left edge. Load-bearing: an activity dated before the
    //     event has its window anchored at the event start, and column 0 is where it belongs.
    //   - later than every column -> the last column, rather than nothing.
    // -1 only when there is no timeline to point at.
    const getColumnIndexForDate = useCallback(
        (date) => {
            if (!date || !columnsKeys.length) {
                return -1;
            }

            const target = moment(date, 'YYYY-MM-DD');
            if (!target.isValid()) {
                return -1;
            }

            const index = columnsKeys.findIndex((key) => {
                const interval = columnDateLookup[key];

                return interval && target.isSameOrBefore(interval.end);
            });

            // Past the last column: land on the end of the timeline rather than nowhere.
            return index === -1 ? columnsKeys.length - 1 : index;
        },
        [columnsKeys, columnDateLookup],
    );

    const getHeadersAndColumnsKeys = useCallback(
        ({ period, periods, excludedPeriods }) => {
            const headersStorageKey = generatePeriodStorageKey(PROJECT_PERIOD_HEADERS_BASE_KEY, eventSlug, period, minDate, maxDate);
            const columnsStorageKey = generatePeriodStorageKey(PROJECT_PERIOD_COLUMNS_BASE_KEY, eventSlug, period, minDate, maxDate);

            // The cache key is built from the event and the format only — it does not encode the
            // exclusions. So it may only serve (and may only be written for) the unexcluded
            // timeline; otherwise a cached full set would be replayed over a narrowed one and the
            // Period range would never trim any columns.
            const cacheable = !Object.keys(excludedPeriods || {}).length;

            const persistedHeaders = cacheable ? localStorage.getItem(headersStorageKey) : null;
            const persistedColumnsKeys = cacheable ? localStorage.getItem(columnsStorageKey) : null;

            if (persistedHeaders && persistedColumnsKeys) {
                try {
                    const parsedHeaders = JSON.parse(persistedHeaders);
                    const parsedColumnsKeys = JSON.parse(persistedColumnsKeys);

                    setHeaders(parsedHeaders);
                    setColumnsKeys(parsedColumnsKeys);

                    return;
                } catch (e) {
                    console.error('Error parsing persisted headers or columns keys:', e);
                }
            }

            if (!periods || !periods.years) {
                return;
            }

            const headers = [];
            const columns = [];

            // ONE header row per view, each cell carrying the whole date. The outer tiers used to
            // write their label only at the first column of the group they covered, so scrolling
            // one column past the start of a year left the year cell blank and nothing on screen
            // said which year — or, in Day view, which month — the visible columns belonged to.
            //
            // Labels are composed from the interval's `start` moment rather than reused from
            // `getTimeIntervals`: its `label`s are the tier-stack ones ('January', 'Sat 11th') and
            // are shared with the Milestone Roadmap and the PDF layout service, so they are not
            // ours to reformat.
            if (period === PERIOD_YEARS) {
                const yearsRow = { columns: {} };
                let index = 0;
                Object.entries(periods.years).forEach(([year, yearData]) => {
                    if (excludedPeriods[year]) {
                        return;
                    }

                    // Already a full date on its own — a year is the whole label.
                    yearsRow.columns[index] = { label: yearData.label };
                    columns.push(year);

                    index++;
                });

                headers.push(yearsRow);
            } else if (period === PERIOD_QUARTERS) {
                const quartersRow = { columns: {} };

                let index = 0;
                Object.values(periods.years).forEach((yearData) => {
                    Object.values(yearData.quarters).forEach((quarterData) => {
                        if (excludedPeriods[quarterData.key]) {
                            return;
                        }

                        quartersRow.columns[index] = { label: `${quarterData.label} ${quarterData.start.format('YYYY')}` };
                        columns.push(quarterData.key);

                        index++;
                    });
                });

                headers.push(quartersRow);
            } else if (period === PERIOD_MONTHS) {
                const monthsRow = { columns: {} };

                let index = 0;
                Object.values(periods.years).forEach((yearData) => {
                    Object.values(yearData.months).forEach((monthData) => {
                        if (excludedPeriods[monthData.key]) {
                            return;
                        }

                        monthsRow.columns[index] = { label: monthData.start.format('MMM YYYY') };
                        columns.push(monthData.key);

                        index++;
                    });
                });

                headers.push(monthsRow);
            } else if (period === PERIOD_DAYS) {
                const daysRow = { columns: {} };

                let index = 0;
                Object.values(periods.years).forEach((yearData) => {
                    Object.values(yearData.months).forEach((monthData) => {
                        Object.values(monthData.days).forEach((dayData) => {
                            if (excludedPeriods[dayData.key]) {
                                return;
                            }

                            daysRow.columns[index] = { label: dayData.start.format('ddd D MMM YYYY') };
                            columns.push(dayData.key);

                            index++;
                        });
                    });
                });

                headers.push(daysRow);
            }

            setHeaders(headers);
            setColumnsKeys(columns);

            if (cacheable) {
                localStorage.setItem(headersStorageKey, JSON.stringify(headers));
                localStorage.setItem(columnsStorageKey, JSON.stringify(columns));
            }
        },
        [period, periods, excludedPeriods, eventSlug, minDate, maxDate],
    );

    useEffect(() => {
        getHeadersAndColumnsKeys({ period, periods, excludedPeriods });
    }, [getHeadersAndColumnsKeys, period, periods, excludedPeriods]);

    useEffect(() => {
        localStorage.setItem(PROJECT_PERIOD_KEY, period);
    }, [period]);

    const changePeriod = useCallback(
        (period) => {
            if (PERIODS_MAP.includes(period)) {
                setPeriod(period);
            }
        },
        [period],
    );

    /**
     * Replace the whole exclusion map at once (`{ periodKey: true }` means "excluded").
     *
     * The Milestone Roadmap's Period range derives the complete set of excluded keys in one go, so
     * the map is set wholesale rather than key by key — and it is the only writer, which is why
     * changing format no longer resets the map here: the roadmap re-derives and re-applies it for
     * the new format instead. The key-set guard keeps the previous object identity when nothing
     * actually changed, so consumers don't recompute their columns on every re-render.
     */
    const setExcludedPeriodsBulk = useCallback((excluded) => {
        setExcludedPeriods((prev) => (haveSameKeys(prev, excluded) ? prev : excluded || {}));
    }, []);

    const value = {
        periods,
        period,
        min: minDate,
        max: maxDate,
        headers,
        columnsKeys,
        columnDateLookup,
        changePeriod,
        setExcludedPeriodsBulk,
        getDateRangeForColumnIndexes,
        getColumnIndexForDate,
    };

    return <ProjectPeriodContext.Provider value={value}>{children}</ProjectPeriodContext.Provider>;
};

export default ProjectPeriodContext;
